/**
 * Utilidades de tiempo basadas en la zona horaria del local (cuenta),
 * independiente del dispositivo donde se ejecute la app.
 *
 * Estrategia: todos los timestamps internos usan "reloj mural del local"
 * codificado como UTC naive (Date.UTC con los dígitos hora del local).
 * Así las comparaciones son correctas aunque el dispositivo esté en otra zona.
 */

/**
 * @param {Object} rootGetters
 * @returns {string|null}
 */
export function resolveLocationTimezone(rootGetters) {
    const fromStore = rootGetters['getTimeZoneName']
    if (fromStore) return fromStore

    const loginLocation = rootGetters['login/getLocation']
    if (loginLocation && loginLocation.timezone_name) {
        return loginLocation.timezone_name
    }

    const localLocation = rootGetters['loginUser/getLocalLocation']
    if (localLocation && localLocation.timezone_name) {
        return localLocation.timezone_name
    }

    return null
}

/**
 * @param {string} timezone
 * @param {Date} [date]
 * @returns {{ year: number, month: number, day: number, hour: number, minute: number, second: number }}
 */
export function getLocationDateParts(timezone, date = new Date()) {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23'
    }).formatToParts(date)

    const get = (type) => parts.find((p) => p.type === type).value

    const year = parseInt(get('year'), 10)
    const month = parseInt(get('month'), 10)
    const day = parseInt(get('day'), 10)
    let hour = parseInt(get('hour'), 10)
    const minute = parseInt(get('minute'), 10)
    const second = parseInt(get('second'), 10)

    // Algunos runtimes ignoran hourCycle y devuelven 24:xx para el mismo día.
    // formatToParts ya informa la fecha correcta, por lo que no se avanza el día.
    if (hour >= 24) {
        hour = 0
    }

    return { year, month, day, hour, minute, second }
}

/**
 * @param {{ year: number, month: number, day: number, hour: number, minute: number, second: number }} parts
 * @returns {number}
 */
export function wallClockToMs(parts) {
    return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second)
}

/**
 * Reloj mural del dispositivo codificado como UTC naive (misma base que alarmas).
 *
 * @param {Date} [instant]
 * @returns {import('moment').Moment}
 */
export function wallClockMomentFromDevice(instant = new Date()) {
    const local = moment(instant)
    return moment.utc(wallClockToMs({
        year: local.year(),
        month: local.month() + 1,
        day: local.date(),
        hour: local.hour(),
        minute: local.minute(),
        second: local.second()
    }))
}

/**
 * Convierte un timestamp real a reloj mural del local codificado como UTC naive.
 * Los timestamps recibidos del API ya representan un instante concreto, así que
 * aquí solo se proyectan a la zona del local, sin corregir el reloj del dispositivo.
 *
 * @param {Object} rootGetters
 * @param {Date|number|string|import('moment').Moment} value
 * @returns {import('moment').Moment}
 */
export function toLocationWallClockMoment(rootGetters, value) {
    let epochMs

    if (moment.isMoment(value)) {
        epochMs = value.valueOf()
    } else if (value instanceof Date) {
        epochMs = value.getTime()
    } else if (typeof value === 'number') {
        epochMs = value < 1e12 ? value * 1000 : value
    } else if (typeof value === 'string' && /^\d+(\.\d+)?$/.test(value.trim())) {
        const numericValue = Number(value)
        epochMs = numericValue < 1e12 ? numericValue * 1000 : numericValue
    } else {
        const parsed = moment(value)
        epochMs = parsed.isValid() ? parsed.valueOf() : NaN
    }

    if (!Number.isFinite(epochMs)) {
        return moment.invalid()
    }

    const instant = new Date(epochMs)
    const timezone = resolveLocationTimezone(rootGetters)

    if (!timezone) {
        return wallClockMomentFromDevice(instant)
    }

    try {
        return moment.utc(wallClockToMs(getLocationDateParts(timezone, instant)))
    } catch (error) {
        console.error('toLocationWallClockMoment error:', error)
        return wallClockMomentFromDevice(instant)
    }
}

/**
 * serverTimeDifference solo es válido cuando el dispositivo está en la zona de la cuenta.
 *
 * @param {string} accountTimezone
 * @returns {boolean}
 */
export function isDeviceInAccountTimezone(accountTimezone) {
    if (!accountTimezone) return false

    try {
        const deviceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
        return deviceTimezone === accountTimezone
    } catch (_) {
        return false
    }
}

/**
 * @param {Object} rootGetters
 * @param {Date} [instant]
 * @returns {import('moment').Moment}
 */
export function getLocationMoment(rootGetters, instant = new Date()) {
    const timezone = resolveLocationTimezone(rootGetters)

    if (!timezone) {
        console.warn('getLocationMoment: timezone del local no disponible, usando reloj mural del dispositivo (UTC naive)')
        return wallClockMomentFromDevice(instant)
    }

    try {
        let date = toLocationWallClockMoment(rootGetters, instant)

        const serverTimeDifference = rootGetters['getServerTimeDifference'] || 0
        const dateLimitation = rootGetters['getDateLimitation']

        // Solo corregir desfase de reloj del dispositivo vs servidor cuando ambos
        // comparten zona horaria; si no, la diferencia mezcla offset de TZ con skew.
        if (
            dateLimitation === 0 &&
            serverTimeDifference !== 0 &&
            isDeviceInAccountTimezone(timezone)
        ) {
            const deviceParts = {
                year: moment(instant).year(),
                month: moment(instant).month() + 1,
                day: moment(instant).date(),
                hour: moment(instant).hour(),
                minute: moment(instant).minute(),
                second: moment(instant).second()
            }
            const deviceWallMs = wallClockToMs(deviceParts)
            const locationWallMs = date.valueOf()
            const clockSkewMinutes = Math.round((locationWallMs - deviceWallMs) / 60000)

            if (clockSkewMinutes !== serverTimeDifference) {
                date = date.add(serverTimeDifference - clockSkewMinutes, 'minutes')
            }
        }

        return date
    } catch (error) {
        console.error('getLocationMoment error:', error)
        return wallClockMomentFromDevice(instant)
    }
}

/**
 * @param {Object} rootGetters
 * @returns {number}
 */
export function getLocationTimestamp(rootGetters) {
    return getLocationMoment(rootGetters).valueOf()
}

/**
 * Convierte "HH:mm" a moment UTC del día del local (+ offset de días).
 *
 * @param {import('moment').Moment} locationNow
 * @param {string} timeStr
 * @param {number} [offsetDays]
 * @returns {import('moment').Moment}
 */
export function parseLocationTimeOnDay(locationNow, timeStr, offsetDays = 0) {
    const [hour, minute] = timeStr.split(':').map(Number)
    return locationNow
        .clone()
        .startOf('day')
        .add(offsetDays, 'days')
        .hour(hour)
        .minute(minute)
        .second(0)
        .millisecond(0)
}

/**
 * @param {import('moment').Moment} locationNow
 * @param {number} float
 * @param {number} [offsetDays]
 * @returns {number}
 */
export function floatToLocationTimestamp(locationNow, float, offsetDays = 0) {
    const totalMinutes = Math.round(Number(float) * 60)
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60

    return locationNow
        .clone()
        .startOf('day')
        .add(offsetDays, 'days')
        .hour(hours)
        .minute(minutes)
        .second(0)
        .millisecond(0)
        .valueOf()
}

/**
 * Parsea fecha del servidor en hora del local (YYYY-MM-DD HH:mm:ss).
 *
 * @param {string} dateString
 * @returns {number|null}
 */
export function parseLocationDateTime(dateString) {
    if (!dateString) return null
    const parsed = moment(String(dateString).trim().substring(0, 19), 'YYYY-MM-DD HH:mm:ss')
    if (!parsed.isValid()) return null
    return Date.UTC(
        parsed.year(),
        parsed.month(),
        parsed.date(),
        parsed.hour(),
        parsed.minute(),
        parsed.second()
    )
}

/**
 * @param {number|null|undefined} value
 * @returns {boolean}
 */
export function isValidServerDateTime(value) {
    return value != null && Number.isFinite(value)
}

/**
 * @param {string|number} value
 * @returns {number|null}
 */
export function parseServerDateTime(value) {
    if (value == null) return null
    if (typeof value === 'number') return value

    const normalized = String(value).trim().replace('T', ' ').substring(0, 19)
    const parsed = parseLocationDateTime(normalized)
    if (parsed != null) return parsed

    const fallback = moment(value)
    return fallback.isValid()
        ? Date.UTC(fallback.year(), fallback.month(), fallback.date(), fallback.hour(), fallback.minute(), fallback.second())
        : null
}

/**
 * Formatea un instante en hora operacional del local (timezone + server offset).
 *
 * @param {Object} rootGetters
 * @param {number} [epochMs] - instante real del dispositivo (Date.now / click)
 * @returns {string}
 */
export function formatLocationDateTime(rootGetters, epochMs) {
    const instant = epochMs != null ? new Date(epochMs) : new Date()
    return getLocationMoment(rootGetters, instant).format('YYYY-MM-DD HH:mm:ss')
}

/**
 * @param {number} ms
 * @returns {string}
 */
export function formatWallClockMs(ms) {
    return moment.utc(ms).format('YYYY-MM-DD HH:mm:ss')
}
